<!-- <script context="module" lang="ts">
  export type ComboDataSource =
    | "demand_partners"
    | "publishers"
    | "campaigns"
    | "adomain_daily"
    | "categories";
  interface SelectItem {
    labal: string;
    value: string;
  }
  interface ManifestOptions {
    pageSize?: number;
    page?: number;
    sortOrder?: string;
    sortBy?: string;
    route?: string;
    filterKey?: string;
    itemParsing?: SelectItem | any;
    selectAllLabel?: string;
  }
  interface Manifest {
    [key: string]: ManifestOptions;
  }
</script> -->
<script>
  /**
   * ComboSelector
   * can render either a simple select box
   * or a multi-tag selector with a
   * with async fetching
   *
   * @example render multi-tag selector
   *  <AutoCompleteCombo
   *     name="categories"
   *     entity="categories"
   *     mode="tags"
   *     placeholder="Choose IAB Category"
   *     value={blacktypeItem.categories}
   *     on:change={onIABChange}
   *   />
   *
   * @example render single-select box
   *    <AutoCompleteCombo
   *       name="demand_partner_id"
   *       entity="demand_partners"
   *       initialValue={blacklistItem.demand_partner_id}
   *       on:change={onDemandPartnerChange}
   *       pageSize={20}
   *     />
   */
  import { createEventDispatcher, getContext, onMount } from "svelte";
  import Select from "svelte-select";
  import { getList } from "../../api";
  const { onFormChange } = getContext("form");
  import { flip } from "@floating-ui/dom";
  // import CustomIcon from "../CustomIcon.svelte";

  const dispatch = createEventDispatcher();

  /**
   * entity represents the source of data to load
   * You can add more entitys and define their fetch functions
   * values: demand_partners, publishers, campaigns, adomain_daily, categories
   */
  export let entity = undefined;
  /**
   * If no entity is set, getItems can be used
   * to provide custom data fetching
   */
  export let getItems = undefined;
  // input id
  export let id = "select";
  export let name;
  // placeholder, label
  export let label = "Select";
  export let placeholder = label;
  /**
   * This doesn't get updated after first setting
   */
  export let formDetection = true;
  export let disabled = false;
  export let gray = false;
  // export let groupBy = null;
  export let isMulti = false;
  export let closeListOnChange = true;
  export let readonlyValue = false;
  export let isSelectAll = false;
  export let isSearchable = true;
  export let isClearable = true;
  export let isUnknown = false;
  export let allowNewItem = false;
  // export let unfilter = true;
  export let itemParsingMethod = null; // method must return array items with label, value fields
  export let containerStyles =
    "min-height:48px; border-color:#9A9AB0; border-radius: 0.375rem; " +
    ((gray || disabled) && "border:0px; background:#d3d3d36e");
  export let containerClasses =
    "relative border rounded-md svelte-1oyjena w-full ";

  let select;
  let filterText = "";
  export let value = [];

  // fetch props, only if entity is set
  const fetchManifest = {
    demand_partners: {
      itemParsing: { label: "name", value: "demand_partner_id" },
      route: "demand",
      selectAllLabel: "All Demand Partners",
    },
    advertisers: {
      itemParsing: { label: "name", value: "demand_partner_id" },
      route: "demand",
      staticFilter:{demand_partner_type:'0'},
      selectAllLabel: "All Demand Partners",
    },
    agency: {
      itemParsing: { label: "name", value: "demand_partner_id" },
      route: "demand",
      staticFilter:{demand_partner_type:'1'},
      selectAllLabel: "All Demand Partners",
    },
    publishers: {
      itemParsing: { label: "name", value: "publisher_id" },
      route: "publisher",
      selectAllLabel: "All Publishers",
    },
    campaigns: {
      itemParsing: { label: "name", value: "campaign_id" },
      route: "campaigns",
      selectAllLabel: "All Campaigns",
    },
    adomain_daily: {
      itemParsing: { label: "adomain", value: "adomain" },
      sortBy: "adomain",
      route: "adomain/dailies",
      filterKey: "adomain",
      selectAllLabel: "All Ad Domains",
    },
    categories: {
      sortBy: "id",
      route: "category",
      filterKey: "search",
      selectAllLabel: "All Categories",
    },
    iab_categories: {
      sortBy: "iab_category_id",
      route: "iab/category",
      filterKey: "search",
      selectAllLabel: "All IAB Categories",
    },
    keywords: {
      itemParsing: { label: "keyword_id", value: "keyword_id" },
      sortBy: "keyword_id",
      route: "keyword",
      filterKey: "contains",
      selectAllLabel: "All Keywords",
    },
    series: {
      itemParsing: { label: "series_id", value: "series_id" },
      sortBy: "series_id",
      filterKey: "contains",
      route: "series",
      selectAllLabel: "All Series items",
    },
  };
  export let options = {
    pageSize: fetchManifest[entity]?.pageSize || 10,
    page: fetchManifest[entity]?.page || 1,
    sortOrder: fetchManifest[entity]?.sortOrder || "ASC",
    sortBy: fetchManifest[entity]?.sortBy || "name",
    route: fetchManifest[entity]?.route || "",
    staticFilter:fetchManifest[entity]?.staticFilter || {},
    filterKey: fetchManifest[entity]?.filterKey || "name",
    itemParsing: fetchManifest[entity]?.itemParsing || {
      label: "name",
      value: "id",
    },
    selectAllLabel: fetchManifest[entity]?.selectAllLabel || "Select All",
  };

  /**
   * initial items to show
   */
  export let items = [];
  // let filteredItems = items;
  function filterItems() {
    if (!isMulti || !value) return;
    try {
      items =  items.filter(
        // (item) => !value.find((e) => getValue(e) === item.value)
        (item) => !value.find((e) => e.value === item.value || item.value === e)
      );
      // filteredItems = filteredList;
    } catch (e) {
      console.error("Filtering error", e);
    }
  }
  /**
   * what component to render
   * select | tags
   **/
  // function checkValueExists(items){
  //   if(!items.includes(value)) value = null
  // }
  // $: checkValueExists(items)
  let loading = false;
  const floatingConfig = {
    middleware: [flip()],
    placement: "bottom-start",
    autoUpdate: true,
  };
  async function fetchItems(filter = undefined) {
    try {
      loading = true;
      const {
        route,
        pageSize,
        page,
        sortBy,
        sortOrder,
        filterKey,
        staticFilter,
        itemParsing,
        selectAllLabel,
      } = options;
      const response = await getList(route, pageSize, page, sortBy, sortOrder, {
        [filterKey]: filter,
        ...staticFilter
      });
      if (response?.data) {
        if (itemParsingMethod != null) {
          items = itemParsingMethod(response.data);
        } else {
          items = response.data.map((item) => {
            return {
              value: item[itemParsing.value],
              label: item[itemParsing.label],
            };
          });
        }
        // items = itemParsingMethod ?  : response.data.map((item) => {

        // if (isMulti && !unfilter) {
        //  items = items;
        //  items = filterItemsFormValue();
        // }
        //  console.log({isSelectAll,selectAllLabel});
        if (isSelectAll) {
          items.unshift({ value: "", label: selectAllLabel });
        }
        //  console.log({ items, value }, "items");
        // filteredItems = items;
        filterItems();
        return items;
      }
      return [];
    } catch (error) {
      console.warn(error);
    } finally {
      loading = false;
    }
  }
  // function filterItemsFormValue() {
  // if (value?.length) {
  // return unfilteredItems.filter(
  // (item) => !value.find((v) => getValue(v) == item.value)
  // );
  // }
  // return unfilteredItems;
  // }
  function handleDataChange(event) {
    if (formDetection) onBlurChange(event);
    const data = event.detail;
    let newValue = data;
    // in case of multi + selectAll

    if (isMulti && (isSelectAll || isUnknown)) {
      if (data?.length) {
        const lastItem = data[data.length - 1];
        if (!lastItem.value || lastItem.value == "unknown") {
          newValue = data.length > 1 ? [lastItem] : data;
        } else {
          newValue = newValue.filter(
            (i) => i.value != "" && i.value != "unknown"
          );
        }
      } else {
        newValue = null;
        select.handleClear();
      }
    }

    value = newValue;
    // if (isMulti) concatenateLabels();
    // console.log({value,items});
    filterItems();
    dispatch("change", value);
  }

  function onBlurChange(e) {
    e.name = name || null;
    onFormChange(e);
  }

  function findInItems(value, items) {
    const values = items.map((item) => item.value);
    const data = [];
    for (const val of value) {
      const i = values.indexOf(val);
      if (i >= 0) {
        data.push(items[i]);
      } else {
        data.push({ label: val, value: val });
      }
    }
    return data;
  }
  onMount(async () => {
    console.log();
    if (!items.length && entity && !getItems) {
      getItems = fetchItems;
      await fetchItems();
    }
    if (isUnknown) {
      items = [{ value: "unknown", label: "Unknown" }, ...items];
    }
    // parse
    if (typeof value === "string") {
      // verify string value
      if (isMulti) {
        // multi value of string -> find and select from items
        const split = value.split(",");
        value = findInItems(split, items);
      } else {
        // single value of string -> find and select from items
        const search = items.find((item) => item.value == value);
        value = search || { label: value, value: value };
      }
    }
    // if (isMulti) concatenateLabels();
    filterItems();
  });

  // add item functionality taken from svelte-select example
  function handleFilter(e) {
    if (e?.detail?.length === 0 && filterText.length > 0 && allowNewItem) {
      const prev = items.filter((i) => !i.created);
      items = [
        ...prev,
        { value: filterText, label: filterText, created: true },
      ];
    }
  }

  function handleChange(e) {
    if (!allowNewItem || entity) return;
    items = items.map((i) => {
      delete i.created;
      return i;
    });
  }

  // function isSelectedValue(searchObject) {
  // if (value == null) {
  // value = [];
  // }
  // return value.find((v) => getValue(v) == searchObject?.value);
  // }

  // function checkSelectedValue(searchObject) {
  // let newValue;
  // if (!isMulti) handleDataChange({ detail: searchObject });
  // else {
  // if (isSelectedValue(searchObject)) {
  // newValue = value.filter(
  // (item) => getValue(item) !== searchObject.value
  // );
  // } else {
  // newValue = [...value, searchObject];
  // }
  // handleDataChange({ detail: newValue });
  // }
  // }

  function handleClearAction() {
    value = isMulti ? [] : null;
    const details = { detail: value, name };
    onBlurChange(details);
    dispatch("change", value);
  }

  // $: selectedValue = { name: "" };

  // const concatenateLabels = () => {
    // if (value == null) {
      // value = [];
    // }
    // try {
      // let name = value.map((v) => v.label || v).join(", ");
      // selectedValue.name = name;
    // } catch (e) {
      // console.log({ error: e });
      // selectedValue.name = "";
    // }
  // };
</script>

<Select
  {containerStyles}
  class={containerClasses}
  items={items}
  {placeholder}
  {id}
  {name}
  {value}
  justValue={readonlyValue}
  searchable={isSearchable}
  multiple={isMulti}
  clearable={isClearable}
  bind:this={select}
  placeholderAlwaysShow
  {floatingConfig}
  loadOptions={getItems}
  {loading}
  showChevron
  on:input={handleDataChange}
  on:change={handleChange}
  on:filter={handleFilter}
  bind:filterText
  filterSelectedItems={false}
  {closeListOnChange}
>
  <div slot="item" let:item>
    <!-- {#if isMulti && isCheckable && isSelectedValue(item)} -->
    <!-- <div> -->
    <!-- <CustomIcon -->
    <!-- class="inline-block" -->
    <!-- width="18" -->
    <!-- height="18" -->
    <!-- name="check" -->
    <!-- color="orange" -->
    <!-- /> -->
    <!-- {item.created && allowNewItem ? "Add new: " : ""} -->
    <!-- {item.label} -->
    <!-- </div> -->
    <!-- {:else} -->
    {item.created && allowNewItem ? "Add new: " : ""}
    {item.label}
    <!-- {/if} -->
  </div>
  <!-- <div slot="selection" let:selection> -->
  <!-- {#if isMulti && isCheckable && value.length} -->
  <!-- {selectedValue.name} -->
  <!-- {:else if selection && !isMulti} -->
  <!-- {selection.label || selection } -->
  <!-- {:else} -->
  <!-- {placeholder} -->
  <!-- {/if} -->
  <!-- </div> -->
</Select>
